import Button from "react-bootstrap/Button";
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Badge from 'react-bootstrap/Badge';
import InputGroup from 'react-bootstrap/InputGroup';
import CmsPageSkeleton from '../components/CmsPageSkeleton';

import {
    FaImage,
    FaEye,
    FaEdit,
    FaTags,
    FaPlus,
} from 'react-icons/fa';
import {
    FiArrowLeft, FiSave, FiGlobe, FiX, FiExternalLink, FiLink,
} from 'react-icons/fi';

import toast from 'react-hot-toast';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/StAuthGiver';
import { callApi, getCurSiteId } from '../lib/Common_funcs.js';

const WhichEnv = (process.env.REACT_APP_TURBO_ENV === undefined ? 'aws' : 'staging');
const Tdebug = (WhichEnv !== 'aws');

export const StCmsEntryEditor = () => {
    const { loginAction, logOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { entryId, schemaId: paramSchemaId } = useParams();

    // Determine schemaId - for /cms/blog/* routes, default to 'blog'
    const schemaId = paramSchemaId || (location.pathname.includes('/cms/blog') ? 'blog' : null);
    const isNewEntry = !entryId || entryId === 'new';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [schema, setSchema] = useState(null);
    const [schemaError, setSchemaError] = useState(null);

    // Validation errors from backend
    const [validationErrors, setValidationErrors] = useState([]);
    const [showErrorAlert, setShowErrorAlert] = useState(false);

    const siteId = getCurSiteId() || '';
    const acctSid = localStorage.getItem('turbosteedAcct') || '';
    const act_e = localStorage.getItem('act_e') || '';

    // Dynamic form data - keyed by field name
    const [formData, setFormData] = useState({});
    const [status, setStatus] = useState('draft');
    const [createdAt, setCreatedAt] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [entrySlug, setEntrySlug] = useState('');
    const [publicUrl, setPublicUrl] = useState(null);

    // For multiselect fields (like tags, hashtags)
    const [multiselectInputs, setMultiselectInputs] = useState({});

    // Preview mode for richtext
    const [previewMode, setPreviewMode] = useState(false);

    // Asset picker modal
    const [showAssetModal, setShowAssetModal] = useState(false);
    const [assets, setAssets] = useState([]);
    const [assetsLoading, setAssetsLoading] = useState(false);
    const [assetTargetField, setAssetTargetField] = useState(null);
    const [hoveredAsset, setHoveredAsset] = useState(null);

    const bodyRefs = useRef({});
    const isMounted = useRef(false);

    // Generate slug from text
    const generateSlug = (text) => {
        if (!text) return '';
        return text
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    // Get display name for schema
    const getSchemaDisplayName = () => {
        if (schema?.display_name) return schema.display_name;
        if (!schemaId) return 'Entry';
        return schemaId.charAt(0).toUpperCase() + schemaId.slice(1).replace(/_/g, ' ');
    };

    // Fetch schema definition
    const fetchSchema = async () => {
        try {
            const url = `/sites/${siteId}/cms/schemas/${schemaId}`;
            const rcJson = await callApi(url, 'GET', {}, {});

            if (rcJson.disposition === 'logout') {
                logOut();
                return null;
            }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                const schemaData = rcJson.respBody.data || rcJson.respBody;
                Tdebug && console.log('Fetched schema:', schemaData);
                return schemaData;
            } else {
                setSchemaError(`Schema '${schemaId}' not found`);
                return null;
            }
        } catch (err) {
            Tdebug && console.log('Error fetching schema:', err);
            setSchemaError('Failed to load schema');
            return null;
        }
    };

    // Initialize form data from schema fields
    const initializeFormData = (schemaData, existingData = {}) => {
        const initial = {};
        const multiselectInit = {};

        for (const field of schemaData.fields || []) {
            // Skip computed fields
            if (field.computed) continue;

            const existingValue = existingData[field.name];

            switch (field.type) {
                case 'multiselect':
                    initial[field.name] = existingValue || [];
                    multiselectInit[field.name] = '';
                    break;
                case 'media':
                    if (field.multiple) {
                        initial[field.name] = existingValue || [];
                    } else {
                        initial[field.name] = existingValue ?? '';
                    }
                    break;
                case 'boolean':
                    initial[field.name] = existingValue ?? false;
                    break;
                case 'number':
                    initial[field.name] = existingValue ?? '';
                    break;
                default:
                    initial[field.name] = existingValue ?? '';
            }
        }

        setFormData(initial);
        setMultiselectInputs(multiselectInit);
    };

    // Fetch entry data if editing
    const fetchEntry = async (schemaData) => {
        try {
            const url = `/sites/${siteId}/cms/schemas/${schemaId}/entries/${entryId}`;
            const rcJson = await callApi(url, 'GET', {}, {});

            if (rcJson.disposition === 'logout') {
                logOut();
                return;
            }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                const entry = rcJson.respBody.data || rcJson.respBody;
                const data = entry.data || entry;

                initializeFormData(schemaData, data);
                setEntrySlug(entry.slug || data.slug || '');
                setStatus(entry.status || 'draft');
                setCreatedAt(entry.created_at);
                setUpdatedAt(entry.updated_at);
                setPublicUrl(entry.public_url || null);

                if (rcJson.respBody?.etac) {
                    loginAction(null, rcJson.respBody.etac);
                }
            } else {
                toast.error('Failed to load entry');
                navigateBack();
            }
        } catch (err) {
            Tdebug && console.log(err);
            toast.error('Failed to load entry');
            navigateBack();
        }
    };

    // Load schema and entry on mount
    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;

        const loadData = async () => {
            setLoading(true);

            const schemaData = await fetchSchema();
            if (!schemaData) {
                setLoading(false);
                return;
            }

            setSchema(schemaData);

            if (isNewEntry) {
                initializeFormData(schemaData);
                setLoading(false);
            } else {
                await fetchEntry(schemaData);
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Auto-generate slug when title changes (for new entries)
    useEffect(() => {
        if (isNewEntry && formData.title && schema) {
            const slugField = schema.fields?.find(f => f.type === 'slug');
            if (slugField) {
                setFormData(prev => ({
                    ...prev,
                    [slugField.name]: generateSlug(formData.title)
                }));
            }
        }
    }, [formData.title, isNewEntry, schema]);

    const navigateBack = () => {
        if (schemaId === 'blog') {
            navigate(`/${siteId}/cms/blog`);
        } else {
            navigate(`/${siteId}/cms/entries/${schemaId}`);
        }
    };

    // Update a form field
    const updateField = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    // Handle multiselect add
    const addMultiselectItem = (fieldName, maxItems) => {
        const inputValue = multiselectInputs[fieldName]?.trim();
        if (!inputValue) return;

        const currentItems = formData[fieldName] || [];
        if (currentItems.includes(inputValue)) {
            toast.error('Item already exists');
            return;
        }
        if (maxItems && currentItems.length >= maxItems) {
            toast.error(`Maximum ${maxItems} items allowed`);
            return;
        }

        updateField(fieldName, [...currentItems, inputValue]);
        setMultiselectInputs(prev => ({ ...prev, [fieldName]: '' }));
    };

    // Handle multiselect remove
    const removeMultiselectItem = (fieldName, item) => {
        const currentItems = formData[fieldName] || [];
        updateField(fieldName, currentItems.filter(i => i !== item));
    };

    // Build entry payload from form data
    const buildPayload = () => {
        const payload = {};

        for (const field of schema.fields || []) {
            if (field.computed) continue;

            const value = formData[field.name];

            // Skip empty optional fields
            if (!field.required && (value === '' || value === null || value === undefined)) {
                continue;
            }

            // Handle arrays
            if (field.type === 'multiselect') {
                payload[field.name] = value || [];
                continue;
            }

            // Handle numbers
            if (field.type === 'number' && value !== '') {
                payload[field.name] = Number(value);
                continue;
            }

            payload[field.name] = value;
        }

        return payload;
    };

    // Validate required fields
    const validateForm = () => {
        for (const field of schema.fields || []) {
            if (field.computed) continue;
            if (!field.required) continue;

            const value = formData[field.name];

            if (value === undefined || value === null || value === '') {
                toast.error(`${field.name.replace(/_/g, ' ')} is required`);
                return false;
            }

            if (field.type === 'multiselect' && (!Array.isArray(value) || value.length === 0)) {
                toast.error(`${field.name.replace(/_/g, ' ')} is required`);
                return false;
            }
        }
        return true;
    };

    const saveEntry = async (shouldPublish = false) => {
        // Clear previous validation errors
        setValidationErrors([]);
        setShowErrorAlert(false);

        if (!validateForm()) return;

        try {
            if (shouldPublish) {
                setPublishing(true);
            } else {
                setSaving(true);
            }

            const entryData = buildPayload();
            Tdebug && console.log('Saving entry data:', entryData);

            let url;
            let method;

            if (isNewEntry) {
                url = `/sites/${siteId}/cms/schemas/${schemaId}/entries`;
                method = 'POST';
            } else {
                url = `/sites/${siteId}/cms/schemas/${schemaId}/entries/${entryId}`;
                method = 'PUT';
            }

            const rcJson = await callApi(url, method, {}, JSON.stringify(entryData));

            if (rcJson.disposition === 'logout') {
                logOut();
                return;
            }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200 || rcJson.httpStatus === '201' || rcJson.httpStatus === 201) {
                const respData = rcJson.respBody.data || rcJson.respBody;
                const savedEntryId = respData?.entry_id || entryId;

                if (shouldPublish && savedEntryId) {
                    const publishUrl = `/sites/${siteId}/cms/schemas/${schemaId}/entries/${savedEntryId}/publish`;
                    const publishRc = await callApi(publishUrl, 'POST', {}, JSON.stringify({}));

                    if (publishRc.httpStatus === '200' || publishRc.httpStatus === 200) {
                        toast.success('Entry published successfully');
                        setStatus('published');
                    } else {
                        toast.error('Saved but failed to publish');
                    }
                } else {
                    toast.success(isNewEntry ? 'Entry created' : 'Entry saved');
                }

                // Navigate back to list page after successful save
                if (schemaId === 'blog') {
                    navigate(`/${siteId}/cms/blog`, { replace: true });
                } else {
                    navigate(`/${siteId}/cms/entries/${schemaId}`, { replace: true });
                }

                if (rcJson.respBody?.etac) {
                    loginAction(null, rcJson.respBody.etac);
                }
            } else {
                // Handle validation errors
                const errorDetails = rcJson.respBody?.error?.details;
                const errorMessage = rcJson.respBody?.error?.message || 'Failed to save entry';

                if (errorDetails && Array.isArray(errorDetails)) {
                    setValidationErrors(errorDetails);
                    setShowErrorAlert(true);
                    toast.error('Please fix the validation errors');
                } else {
                    setValidationErrors([errorMessage]);
                    setShowErrorAlert(true);
                    toast.error(errorMessage);
                }
            }
        } catch (err) {
            Tdebug && console.log(err);
            setValidationErrors(['An unexpected error occurred. Please try again.']);
            setShowErrorAlert(true);
            toast.error('Failed to save entry');
        } finally {
            setSaving(false);
            setPublishing(false);
        }
    };

    // Fetch assets for image picker
    const fetchAssets = async () => {
        try {
            setAssetsLoading(true);
            const baseUrl = `https://${process.env.REACT_APP_HOST_URL}/users/${acctSid}/sites/${siteId}/assets`;

            const allFiles = [];
            let startAt = '';
            // Paginate through every page so all assets load (API returns a `startAt` cursor).
            for (let i = 0; i < 50; i++) {
                const fetchUrl = startAt ? `${baseUrl}?startAt=${encodeURIComponent(startAt)}` : baseUrl;
                const response = await fetch(fetchUrl, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Authorization': 'Basic ' + btoa(acctSid + ':' + act_e),
                    }
                });

                if (!response.ok) break;

                const data = await response.json();
                allFiles.push(...(data.files || []));
                startAt = data.startAt || '';
                if (!startAt) break;
            }

            const imageFiles = allFiles.filter(file => {
                const ext = file.filename?.split('.').pop()?.toLowerCase() || '';
                return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
            });
            setAssets(imageFiles);
        } catch (err) {
            Tdebug && console.log(err);
            toast.error('Failed to load assets');
        } finally {
            setAssetsLoading(false);
        }
    };

    const openAssetPicker = (fieldName) => {
        setAssetTargetField(fieldName);
        setShowAssetModal(true);
        fetchAssets();
    };

    const insertAsset = (asset) => {
        if (!assetTargetField) return;

        const field = schema.fields?.find(f => f.name === assetTargetField);
        if (!field) return;

        if (field.type === 'media') {
            if (field.multiple) {
                const current = formData[assetTargetField] || [];
                updateField(assetTargetField, [...current, asset.fileUrl]);
            } else {
                updateField(assetTargetField, asset.fileUrl);
            }
        } else if (field.type === 'richtext') {
            const imageMarkdown = `![${asset.filename}](${asset.fileUrl})`;
            const currentValue = formData[assetTargetField] || '';
            updateField(assetTargetField, currentValue + '\n' + imageMarkdown);
        }

        setShowAssetModal(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    // Render a form field based on its type
    const renderField = (field) => {
        const value = formData[field.name];
        const isRequired = field.required;

        switch (field.type) {
            case 'text':
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {isRequired && <span className="text-danger">*</span>}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={`Enter ${field.name.replace(/_/g, ' ')}...`}
                            value={value || ''}
                            onChange={(e) => updateField(field.name, e.target.value)}
                            style={{ fontSize: '13px' }}
                            maxLength={field.max_length}
                        />
                        {field.max_length && (
                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                {(value || '').length}/{field.max_length} characters
                            </Form.Text>
                        )}
                    </Form.Group>
                );

            case 'slug':
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            Slug
                            {isRequired && <span className="text-danger">*</span>}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="auto-generated-slug"
                            value={value || ''}
                            onChange={(e) => {
                                // Allow typing freely, only sanitize invalid chars (keep hyphens)
                                const sanitized = e.target.value
                                    .toLowerCase()
                                    .replace(/[^a-z0-9-]/g, '-')
                                    .replace(/-+/g, '-');
                                updateField(field.name, sanitized);
                            }}
                            onBlur={(e) => {
                                // Clean up leading/trailing hyphens on blur
                                const cleaned = (e.target.value || '').replace(/^-+|-+$/g, '');
                                updateField(field.name, cleaned);
                            }}
                            style={{ fontSize: '13px', fontFamily: 'monospace' }}
                        />
                    </Form.Group>
                );

            case 'richtext':
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 0 }}>
                                {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                {isRequired && <span className="text-danger">*</span>}
                            </Form.Label>
                            <div className="d-flex gap-1">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => openAssetPicker(field.name)}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                >
                                    <FaImage style={{ fontSize: '11px' }} />
                                </Button>
                                <Button
                                    variant={previewMode ? "primary" : "outline-secondary"}
                                    size="sm"
                                    onClick={() => setPreviewMode(!previewMode)}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                >
                                    {previewMode ? <FaEdit style={{ fontSize: '11px' }} /> : <FaEye style={{ fontSize: '11px' }} />}
                                </Button>
                            </div>
                        </div>
                        {previewMode ? (
                            <div
                                className="border rounded p-3 bg-white"
                                style={{ minHeight: '200px', fontSize: '13px' }}
                                dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(value || '') }}
                            />
                        ) : (
                            <Form.Control
                                as="textarea"
                                ref={el => bodyRefs.current[field.name] = el}
                                placeholder={`Write ${field.name.replace(/_/g, ' ')}...`}
                                value={value || ''}
                                onChange={(e) => updateField(field.name, e.target.value)}
                                style={{
                                    minHeight: '200px',
                                    fontFamily: 'monospace',
                                    fontSize: '13px'
                                }}
                            />
                        )}
                    </Form.Group>
                );

            case 'number':
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {isRequired && <span className="text-danger">*</span>}
                        </Form.Label>
                        <Form.Control
                            type="number"
                            placeholder={`Enter ${field.name.replace(/_/g, ' ')}...`}
                            value={value || ''}
                            onChange={(e) => updateField(field.name, e.target.value)}
                            style={{ fontSize: '13px' }}
                        />
                    </Form.Group>
                );

            case 'boolean':
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Check
                            type="switch"
                            id={`switch-${field.name}`}
                            label={field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            checked={value || false}
                            onChange={(e) => updateField(field.name, e.target.checked)}
                            style={{ fontSize: '13px' }}
                        />
                    </Form.Group>
                );

            case 'select':
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {isRequired && <span className="text-danger">*</span>}
                        </Form.Label>
                        {field.ui === 'radio' ? (
                            <div className="d-flex flex-column gap-1">
                                {(field.options || []).map((opt, idx) => (
                                    <Form.Check
                                        key={idx}
                                        type="radio"
                                        id={`${field.name}-${idx}`}
                                        name={field.name}
                                        label={opt}
                                        value={opt}
                                        checked={value === opt}
                                        onChange={() => updateField(field.name, opt)}
                                        style={{ fontSize: '13px' }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Form.Select
                                value={value || ''}
                                onChange={(e) => updateField(field.name, e.target.value)}
                                style={{ fontSize: '13px' }}
                            >
                                <option value="">Select {field.name.replace(/_/g, ' ')}...</option>
                                {(field.options || []).map((opt, idx) => (
                                    <option key={idx} value={opt}>{opt}</option>
                                ))}
                            </Form.Select>
                        )}
                    </Form.Group>
                );

            case 'multiselect': {
                const items = value || [];
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            <FaTags className="me-1" />
                            {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {isRequired && <span className="text-danger">*</span>}
                        </Form.Label>
                        {field.ui === 'checkbox' ? (
                            <div className="d-flex flex-column gap-1">
                                {(field.options || []).map((opt, idx) => (
                                    <Form.Check
                                        key={idx}
                                        type="checkbox"
                                        id={`${field.name}-${idx}`}
                                        label={opt}
                                        checked={items.includes(opt)}
                                        onChange={(e) => {
                                            updateField(field.name, e.target.checked
                                                ? [...items, opt]
                                                : items.filter(i => i !== opt)
                                            );
                                        }}
                                        style={{ fontSize: '13px' }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <>
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="text"
                                        placeholder={`Add ${field.name.replace(/_/g, ' ')}...`}
                                        value={multiselectInputs[field.name] || ''}
                                        onChange={(e) => setMultiselectInputs(prev => ({ ...prev, [field.name]: e.target.value }))}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addMultiselectItem(field.name, field.max_items);
                                            }
                                        }}
                                        style={{ fontSize: '13px' }}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => addMultiselectItem(field.name, field.max_items)}
                                        style={{ fontSize: '12px' }}
                                    >
                                        <FaPlus />
                                    </Button>
                                </InputGroup>
                                {items.length > 0 && (
                                    <div className="mt-2 d-flex flex-wrap gap-1">
                                        {items.map((item, idx) => (
                                            <Badge
                                                key={idx}
                                                bg="secondary"
                                                className="d-flex align-items-center gap-1 px-2 py-1"
                                                style={{ cursor: 'pointer', fontSize: '11px' }}
                                                onClick={() => removeMultiselectItem(field.name, item)}
                                            >
                                                {item} <span className="ms-1">&times;</span>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                                {field.max_items && (
                                    <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                        {items.length}/{field.max_items} items
                                    </Form.Text>
                                )}
                            </>
                        )}
                    </Form.Group>
                );
            }

            case 'media':
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {isRequired && <span className="text-danger">*</span>}
                        </Form.Label>
                        {value && !field.multiple ? (
                            <div className="mb-2">
                                <img
                                    src={value}
                                    alt={field.name}
                                    className="img-fluid rounded mb-2"
                                    style={{ maxHeight: '150px', objectFit: 'cover' }}
                                />
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => updateField(field.name, '')}
                                >
                                    Remove
                                </Button>
                            </div>
                        ) : field.multiple && Array.isArray(value) && value.length > 0 ? (
                            <div className="mb-2">
                                <div className="d-flex flex-wrap gap-2 mb-2">
                                    {value.map((url, idx) => (
                                        <div key={idx} className="position-relative">
                                            <img
                                                src={url}
                                                alt={`${field.name}-${idx}`}
                                                className="rounded"
                                                style={{ height: '80px', objectFit: 'cover' }}
                                            />
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                className="position-absolute"
                                                style={{ top: '-8px', right: '-8px', padding: '2px 6px', fontSize: '10px' }}
                                                onClick={() => updateField(field.name, value.filter((_, i) => i !== idx))}
                                            >
                                                &times;
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                {(!field.max_items || value.length < field.max_items) && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => openAssetPicker(field.name)}
                                    >
                                        <FaImage className="me-1" /> Add More
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Button
                                variant="outline-primary"
                                className="w-100"
                                style={{ fontSize: '12px' }}
                                onClick={() => openAssetPicker(field.name)}
                            >
                                <FaImage className="me-2" />
                                Select from Assets
                            </Button>
                        )}
                    </Form.Group>
                );

            case 'datetime':
            case 'date':
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {isRequired && <span className="text-danger">*</span>}
                        </Form.Label>
                        <Form.Control
                            type={field.type === 'datetime' ? 'datetime-local' : 'date'}
                            value={value || ''}
                            onChange={(e) => updateField(field.name, e.target.value)}
                            style={{ fontSize: '13px' }}
                        />
                    </Form.Group>
                );

            // case 'reference':
            //     return (
            //         <Form.Group key={field.name} className="mb-0">
            //             <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
            //                 {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            //                 {isRequired && <span className="text-danger">*</span>}
            //             </Form.Label>
            //             <Form.Control
            //                 type="text"
            //                 placeholder={`Enter ${field.ref_type || 'reference'} ID...`}
            //                 value={value || ''}
            //                 onChange={(e) => updateField(field.name, e.target.value)}
            //                 style={{ fontSize: '13px', fontFamily: 'monospace' }}
            //             />
            //             <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
            //                 Reference to {field.ref_type || 'another entry'}
            //             </Form.Text>
            //         </Form.Group>
            //     );

            default:
                return (
                    <Form.Group key={field.name} className="mb-0">
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {isRequired && <span className="text-danger">*</span>}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={`Enter ${field.name.replace(/_/g, ' ')}...`}
                            value={value || ''}
                            onChange={(e) => updateField(field.name, e.target.value)}
                            style={{ fontSize: '13px' }}
                        />
                    </Form.Group>
                );
        }
    };

    // Simple Markdown preview
    const renderMarkdownPreview = (text) => {
        if (!text) return '';
        let html = text
            .replace(/^### (.*$)/gim, '<h3>$1</h3>')
            .replace(/^## (.*$)/gim, '<h2>$1</h2>')
            .replace(/^# (.*$)/gim, '<h1>$1</h1>')
            .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/gim, '<em>$1</em>')
            .replace(/!\[(.*?)\]\((.*?)\)/gim, '<img src="$2" alt="$1" style="max-width:100%;height:auto;">')
            .replace(/\[(.*?)\]\((.*?)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
            .replace(/\n/gim, '<br>');
        return html;
    };

    // Separate fields into main content and sidebar
    const getFieldGroups = () => {
        if (!schema?.fields) return { mainFields: [], sidebarFields: [] };

        const mainFieldTypes = ['richtext', 'text', 'slug'];
        const sidebarFieldTypes = ['select', 'multiselect', 'media', 'datetime', 'date', 'boolean', /*'reference',*/ 'number'];

        // Primary content fields (title, slug, main content) go in main area
        const mainFields = schema.fields.filter(f => {
            if (f.computed) return false;
            if (f.name === 'title' || f.name === 'slug') return true;
            if (f.type === 'richtext') return true;
            // Long text fields
            if (f.type === 'text' && f.max_length && f.max_length > 100) return true;
            return false;
        });

        // Other fields go in sidebar
        const sidebarFields = schema.fields.filter(f => {
            if (f.computed) return false;
            if (mainFields.includes(f)) return false;
            return true;
        });

        return { mainFields, sidebarFields };
    };

    if (loading) {
        return <CmsPageSkeleton />;
    }

    if (schemaError) {
        return (
            <div className="app-content">
                <div className="bento-card" style={{ borderColor: 'var(--error)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px' }}>
                        <FiX size={18} style={{ color: 'var(--error)', flexShrink: 0, marginTop: '2px' }} />
                        <div>
                            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>Error</div>
                            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>{schemaError}</p>
                        </div>
                    </div>
                    <button onClick={navigateBack} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text-primary)' }}>
                        <FiArrowLeft size={14} /> Go Back
                    </button>
                </div>
            </div>
        );
    }

    const { mainFields, sidebarFields } = getFieldGroups();

    const footerBtn = {
        display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px',
        borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500,
        cursor: 'pointer', fontFamily: 'inherit',
    };

    return (
        <div className="app-content animate-in">
            {/* Header */}
            <div className="dashboard-header" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <button
                    onClick={navigateBack}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}
                >
                    <FiArrowLeft size={16} />
                </button>
                <div>
                    <h1 className="dashboard-title" style={{ marginBottom: 0 }}>
                        {isNewEntry ? `New ${getSchemaDisplayName()}` : `Edit ${getSchemaDisplayName()}`}
                    </h1>
                    {!isNewEntry && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                            {status === 'published'
                                ? <span className="badge-modern badge-modern-success">Published</span>
                                : <span className="badge-modern" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>Draft</span>}
                            {entrySlug && <span style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-tertiary)' }}>/{entrySlug}</span>}
                            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Last updated: {formatDate(updatedAt)}</span>
                            {publicUrl && status === 'published' && (
                                <a
                                    href={publicUrl.customDomain || publicUrl.subdomain}
                                    target="_blank" rel="noopener noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--accent-primary)', textDecoration: 'none' }}
                                >
                                    <FiLink size={11} />
                                    {publicUrl.customDomain || publicUrl.subdomain}
                                    <FiExternalLink size={10} />
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Validation Errors */}
            {showErrorAlert && validationErrors.length > 0 && (
                <div style={{ background: 'var(--error-soft)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-md)', padding: '14px 16px', marginBottom: '24px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>Please fix the following errors:</div>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--error)' }}>
                        {validationErrors.map((error, idx) => <li key={idx}>{error}</li>)}
                    </ul>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: sidebarFields.length > 0 ? 'minmax(0, 2fr) minmax(0, 1fr)' : '1fr', gap: '16px' }}>
                {/* Main Content */}
                <div className="bento-card" style={{ padding: 0, overflow: 'visible' }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>Content</div>
                    <div style={{ padding: '16px' }}>
                        {mainFields.map(field => (
                            <div key={field.name} className="cms-field-group">
                                {renderField(field)}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sidebar */}
                {sidebarFields.length > 0 && (
                    <div className="bento-card" style={{ padding: 0, overflow: 'visible', alignSelf: 'flex-start' }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>Details</div>
                        <div style={{ padding: '16px' }}>
                            {sidebarFields.map(field => (
                                <div key={field.name} className="cms-field-group">
                                    {renderField(field)}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                <button onClick={navigateBack} disabled={saving || publishing} style={{ ...footerBtn, background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                    <FiX size={14} />Cancel
                </button>
                <button onClick={() => saveEntry(false)} disabled={saving || publishing} style={{ ...footerBtn, background: 'var(--bg-secondary)', border: '1px solid var(--accent-primary)', color: 'var(--accent-primary)' }}>
                    {saving ? <><Spinner size="sm" />Saving...</> : <><FiSave size={14} />Save Draft</>}
                </button>
                <button onClick={() => saveEntry(true)} disabled={saving || publishing} style={{ ...footerBtn, background: 'var(--accent-gradient)', border: 'none', color: 'white' }}>
                    {publishing ? <><Spinner size="sm" />Publishing...</> : <><FiGlobe size={14} />Publish</>}
                </button>
            </div>

            {/* Asset Picker Modal */}
            <Modal show={showAssetModal} onHide={() => setShowAssetModal(false)} size="lg" centered dialogClassName="cms-asset-modal modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '15px', fontWeight: 600 }}>Select Image</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {assetsLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <Spinner animation="border" style={{ width: '1.5rem', height: '1.5rem', color: 'var(--accent-primary)' }} />
                            <div style={{ marginTop: '10px', fontSize: '13px', color: 'var(--text-tertiary)' }}>Loading assets...</div>
                        </div>
                    ) : assets.length === 0 ? (
                        <div style={{ background: 'var(--accent-soft)', borderRadius: 'var(--radius-md)', padding: '12px 14px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                            No images found. Upload images in the Asset Library first.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
                            {assets.map((asset, index) => {
                                const isHovered = hoveredAsset === index;
                                return (
                                    <div
                                        key={index}
                                        onMouseEnter={() => setHoveredAsset(index)}
                                        onMouseLeave={() => setHoveredAsset(null)}
                                        onClick={() => insertAsset(asset)}
                                        style={{
                                            cursor: 'pointer', borderRadius: 'var(--radius-md)', overflow: 'hidden',
                                            border: isHovered ? '1px solid var(--accent-primary)' : '1px solid var(--border-color)',
                                            boxShadow: isHovered ? '0 0 0 3px var(--accent-soft)' : 'none',
                                            transform: isHovered ? 'translateY(-2px)' : 'none',
                                            transition: 'all 0.15s ease', background: 'var(--bg-secondary)',
                                        }}
                                    >
                                        <div style={{ backgroundImage: `url('${asset.fileUrl}')`, height: '120px', backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: 'var(--bg-tertiary)' }} />
                                        <div style={{ padding: '8px', fontSize: '11px', color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {asset.filename}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowAssetModal(false)}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StCmsEntryEditor;
